import React, { useEffect, useState, useRef } from 'react';
import { getDataReader, RPWDataReader } from "../../livedataFuncs";
import { Col, Row, OverlayTrigger, Tooltip, Nav, Tab, Button } from "react-bootstrap";
import { Avatar } from "../../Components/ui/avatar/avatar";
import { FormatDateDataType, dateToXMLDate, differenceInDays } from "../../util/genericDateAndTimeFuncs";
import { lddGlobalSessionInfo, NavObjectType, ProvincesShortCode } from "../navigationTypes";
import { useAppSelector } from "../../redux/hooks";
import KeyDate from './KeyDate';
import { useDispatch } from "react-redux";
import { lddStateObject, setInfileTasks, setPreference, setTaskPreferences } from '../../redux/reducer';
import { isPageInDR } from '../../dr/Components/drFuncs';
import { isPageInDrPasscodeFile } from '../../dr/Components/drFuncs';
import { TaskFuncs } from '../Settings/TaskManagementSettings/taskFuncs';
import styles from './KeyDate.module.scss'; // Import the CSS file
import { runInnerFunction } from '../../compatManager';
import MilestonesTab from './MilestonesTab';
import TasklistTab from './TasklistTab';
import EditTaskModal from './EditTaskModal';
import Dashboard from './Dashboard';
import { getInitials, getUserColor, getTaskStatus, parseTaskTitle } from './taskUtils';
import EditApptModal from './EditApptModal';

export type TKeyDate = {
    id: number,
    title?: string,
    titleFunc?: (...arg: any[]) => string,
    titleFuncCheckPSM?: boolean,
    psm: string,
    dateFunc?: string,
    province?: ProvincesShortCode[]
}

interface IProps {
    datarootContainer: any,
    page: NavObjectType,
    sidebarshow: string,
    toggleState: boolean,
}

export const RightSideKeyDates = (props: IProps) => {
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const userID = useAppSelector((_state) => SESSION_INFO?.firm.id);
    const pinnedTab = useAppSelector((g: lddStateObject) => g.Preference["PinRightSideTab"]);
    let progressBarTask = useAppSelector((state) => state.ProgressBarTask);
    const progressBarTaskRef = useRef(progressBarTask);
    const [tasks, setTasks] = useState<any[]>([]);
    const [taskProfiles, setTaskProfiles] = useState<any[]>([]);
    const [taskSettings, setTaskSettings] = useState<any>({});
    const [provinceTasks, setProvinceTasks] = useState<any[]>([])
    const [allLawyers, setAllLawyers] = useState<any[]>([]);
    const [appointments, setAppointments] = useState<any[]>([]);
    const [showEditApptModal, setShowEditApptModal] = useState(false);
    const [appointmentToEdit, setAppointmentToEdit] = useState<any>(null);
    const [showDeleteAppModal, setShowDeleteAppModal] = useState(false);
    
    const taskFuncs = new TaskFuncs();
    const dispatch = useDispatch();
    
    // New state for the tabs and edit modal
    const [activeTab, setActiveTab] = useState('milestones');
    const [showAllDates, setShowAllDates] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState<any>(null);
    const [taskDate, setTaskDate] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Add new state for the delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Add a new state variable for the filter
    const [filterByUser, setFilterByUser] = useState<string[]>([]);

    let myTasks = [];

    useEffect(() => {
        if (SESSION_INFO && 
            props.datarootContainer?.dataRoot !== undefined && 
            !isPageInDR(props.page)) {
            const tempDataReader = getDataReader(props.datarootContainer?.dataRoot);
            const AppID = tempDataReader.getAppId?.() || "";
            
            KeyDate.LoadInFileTasks(AppID, SESSION_INFO)
                .then(fileTasks => {
                    // Store all tasks without filtering
                    setTasks(fileTasks);
                })
                .catch(error => {
                    console.error("Error loading in-file key dates:", error);
                    setTasks([]);
                });
        }
    }, [SESSION_INFO, props.datarootContainer?.dataRoot]);

    useEffect(() => {
        function getProvinceTasks() {
            try {
                const result = [];
                const province = SESSION_INFO?.appProvinceShort;
                KeyDate.setProv(province);
                const keydates = KeyDate.KeyDatesDefine.dates;
                for (const keydate of keydates) {
                    const isValidProvince = !keydate.province || keydate.province.includes(province);
                    if(isValidProvince){
                        if(!keydate.title && keydate.titleFunc){
                            keydate.title = keydate.titleFunc();
                        }
                        result.push(keydate);
                    }
                }
                setProvinceTasks(result);
            } catch (error) {
                console.error('Error fetching province tasks:', error);
            }
        }
        
        async function fetchTaskProfiles() {
            try {
                const result = await KeyDate.getTaskProfiles(SESSION_INFO);
                setTaskProfiles(result.taskProfiles);
                setTaskSettings(result.taskSettings);
                dispatch(setTaskPreferences(result.taskProfiles));
            } catch (error) {
                console.error('Error fetching task profiles:', error);
            }
        }
        
        async function fetchUsers() {
            try {
                const usersList = await KeyDate.getUsers(userID.toString(), SESSION_INFO);
                setAllLawyers(usersList);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        }
        
        getProvinceTasks();
        fetchTaskProfiles();
        fetchUsers();
    }, [])

    useEffect(() => {
        progressBarTaskRef.current = progressBarTask;
    }, [progressBarTask]);

    useEffect(() => {
        if (myTasks) {
            const tasksCopy = myTasks.map(task => ({ ...task }));
            tasksCopy.forEach(task => {
                if(task?.typeID === progressBarTaskRef.current?.typeID){
                    task.isCompleted = progressBarTaskRef.current?.isCompleted;
                }
            });
            dispatch(setInfileTasks(tasksCopy));
        }
        if(progressBarTaskRef.current){
            setTasks(myTasks.map(task => {
                if(task?.typeID === progressBarTaskRef.current?.typeID){
                    return { ...task, isCompleted: progressBarTaskRef.current.isCompleted };
                }
                return task;
            }));
            progressBarTaskRef.current = null;
        }
    }, [myTasks, dispatch]);

    // Close action menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openActionMenu !== null) {
                setOpenActionMenu(null);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openActionMenu]);

    // Add new useEffect to fetch appointments
    useEffect(() => {
        if (SESSION_INFO && 
            props.datarootContainer?.dataRoot !== undefined && 
            !isPageInDR(props.page)) {
            const tempDataReader = getDataReader(props.datarootContainer?.dataRoot);
            const AppID = tempDataReader.getAppId?.() || "";
            
            // Fetch appointments for this specific record
            fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/calendarappts?recordID=${AppID}&SessionID=${SESSION_INFO.sid}`)
                .then(response => response.json())
                .then(data => {
                    if (data.ok) {
                        setAppointments(data.result || []);
                    }
                })
                .catch(error => {
                    console.error("Error loading appointments:", error);
                    setAppointments([]);
                });
        }
    }, [SESSION_INFO, props.datarootContainer?.dataRoot]);

    if (isPageInDrPasscodeFile(props.page)) {
        KeyDate.setProv("ON");
    } else {
        KeyDate.setProv(SESSION_INFO?.appProvinceShort);
    }

    let drawerClasses = '';
    if (props.sidebarshow === 'KeyDates' && props.toggleState === true) {
        drawerClasses = ' open'
    }
    
    if (props.datarootContainer?.dataRoot === undefined || isPageInDR(props.page)) {
        return <></>
    }
    
    const DataReaderObj: RPWDataReader = getDataReader(props.datarootContainer?.dataRoot);
    const myDates = KeyDate.getApplicableDates(DataReaderObj);
    myTasks = KeyDate.getApplicableTasks(DataReaderObj, myDates, tasks, taskProfiles, allLawyers, provinceTasks, taskFuncs, taskSettings, SESSION_INFO?.appProvinceShort);
    
    // Process all dates and tasks
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    // Combined data structure for dates and tasks
    interface GroupedItem {
        date: Date;
        formattedDate: string;
        items: Array<{
            type: 'date' | 'task' | 'appointment';
            data: any;
        }>;
        isToday: boolean;
        isPast: boolean;
    }
    
    // Function to format date as "Month Day" (e.g., "February 23")
    const formatDateHeader = (date: Date): string => {
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    };

    // Function to check if two dates are the same day
    const isSameDay = (date1: Date, date2: Date): boolean => {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    };
    
    // Group items by date
    const groupItemsByDate = () => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        
        const dateMap = new Map<string, GroupedItem>();
        
        // Filter out dates with null, undefined, or empty date values
        const validDates = myDates.filter(date => 
            date.date !== null && 
            date.date !== undefined && 
            date.date !== ""
        );
        
        // Process dates
        for (const thisDate of validDates) {
            try {
                // Parse the date parts explicitly to avoid timezone issues
                const [year, month, day] = thisDate.date.split('-').map(Number);
                const dateObj = new Date(year, month - 1, day); // month is 0-indexed in JS
                
                if (isNaN(dateObj.getTime())) continue;
                
                const dateKey = dateObj.toISOString().split('T')[0];
                const isToday = isSameDay(dateObj, currentDate);
                const isPast = dateObj < currentDate;
                
                if (!dateMap.has(dateKey)) {
                    dateMap.set(dateKey, {
                        date: dateObj,
                        formattedDate: formatDateHeader(dateObj),
                        items: [],
                        isToday,
                        isPast
                    });
                }
                
                // Keep track of whether this date item needs to be added or combined
                let shouldAddItem = true;
                
                // Check if this is a Closing or Adjustment item
                if (thisDate.title === "Closing" || thisDate.title === "Adjustment") {
                    // Look for existing Closing or Adjustment items on this date
                    const existingItems = dateMap.get(dateKey).items;
                    for (let i = 0; i < existingItems.length; i++) {
                        const item = existingItems[i];
                        if (item.type === 'date') {
                            // If we find the other type (Closing or Adjustment), combine them
                            if ((thisDate.title === "Closing" && item.data.title === "Adjustment") ||
                                (thisDate.title === "Adjustment" && item.data.title === "Closing")) {
                                
                                // Replace the existing item with a combined one
                                existingItems[i] = {
                                    type: 'date',
                                    data: {
                                        title: "Closing/Adjustment",
                                        day: thisDate.date,
                                        days: differenceInDays(dateToXMLDate(currentDate), thisDate.date)
                                    }
                                };
                                
                                // Don't add a new item
                                shouldAddItem = false;
                                break;
                            }
                        }
                    }
                }
                
                // Add the item if it wasn't combined
                if (shouldAddItem) {
                    dateMap.get(dateKey).items.push({
                        type: 'date',
                        data: {
                            title: thisDate.title,
                            day: thisDate.date,
                            days: differenceInDays(dateToXMLDate(currentDate), thisDate.date)
                        }
                    });
                }
            } catch (e) {
                console.error(`Error processing date: ${thisDate.date}`, e);
            }
        }
        
        // Filter tasks for display purposes only
        const validTasks = myTasks.filter(task => {
            // Display filter - only show tasks that are active and not overwritten
            if (task.isActivated !== 1) {
                return false;
            }
            
            // Check for overwriteDate first, and use it if available
            const dateToUse = task.date;
            return dateToUse !== null && dateToUse !== undefined && dateToUse !== "";
        });
        
        // Process tasks
        for (const task of validTasks) {
            try {
                // Check for overwriteDate first, and use it if available
                const dateToUse = task.date;
                
                let taskDate: Date;
                
                // Ensure proper date parsing
                const dateParts = dateToUse.split('-');
                if (dateParts.length === 3) {
                    // Parse as YYYY-MM-DD
                    const year = parseInt(dateParts[0], 10);
                    const month = parseInt(dateParts[1], 10) - 1; // Months are 0-indexed in JS
                    const day = parseInt(dateParts[2], 10);
                    
                    taskDate = new Date(year, month, day);
                } else {
                    // Try standard JS date parsing as fallback
                    taskDate = new Date(dateToUse);
                }
                
                // Skip invalid dates
                if (isNaN(taskDate.getTime())) continue;
                
                // Normalize date for grouping (remove time component)
                taskDate.setHours(0, 0, 0, 0);
                const dateKey = taskDate.toISOString().split('T')[0];
                const isToday = isSameDay(taskDate, currentDate);
                const isPast = taskDate < currentDate;
                
                if (!dateMap.has(dateKey)) {
                    dateMap.set(dateKey, {
                        date: taskDate,
                        formattedDate: formatDateHeader(taskDate),
                        items: [],
                        isToday,
                        isPast
                    });
                }
                
                dateMap.get(dateKey).items.push({
                    type: 'task',
                    data: task
                });
            } catch (e) {
                console.error(`Error processing task date: ${task.date}`, e);
            }
        }
        
        // Process appointments
        for (const appointment of appointments) {
            try {
                const dateToUse = appointment.calendarItemDate;
                
                if (dateToUse === null || dateToUse === undefined || dateToUse === "") {
                    continue;
                }
                
                const [year, month, day] = dateToUse.split('-').map(Number);
                const appointmentDate = new Date(year, month - 1, day);
                
                if (isNaN(appointmentDate.getTime())) continue;
                
                // Only include future appointments
                // if (appointmentDate < currentDate) continue;
                
                appointmentDate.setHours(0, 0, 0, 0);
                const dateKey = appointmentDate.toISOString().split('T')[0];
                const isToday = isSameDay(appointmentDate, currentDate);
                const isPast = appointmentDate < currentDate;
                
                if (!dateMap.has(dateKey)) {
                    dateMap.set(dateKey, {
                        date: appointmentDate,
                        formattedDate: formatDateHeader(appointmentDate),
                        items: [],
                        isToday,
                        isPast
                    });
                }
                
                dateMap.get(dateKey).items.push({
                    type: 'appointment',
                    data: appointment
                });
            } catch (e) {
                console.error(`Error processing appointment date: ${appointment.calendarItemDate}`, e);
            }
        }
        
        // Convert map to array and sort by date
        return Array.from(dateMap.values())
            .sort((a, b) => a.date.getTime() - b.date.getTime());
    };
    
    const groupedItems = groupItemsByDate();
    
    // Filter overdue incomplete tasks too
    const overdueIncompleteTasks = showAllDates ? [] : myTasks
        .filter(task => {
            // Display filter - only show tasks that are active and not overwritten
            if (task.isActivated !== 1) {
                return false;
            }
            
            try {
                // Check for overwriteDate first, and use it if available
                const dateToUse = task.date;
                
                // Skip tasks with invalid dates
                if (dateToUse === null || dateToUse === undefined || dateToUse === "") {
                    return false;
                }
                
                // const taskDate = new Date(dateToUse);
                const [year, month, day] = dateToUse.split('-').map(Number);
                const taskDate = new Date(year, month - 1, day); // month is 0-indexed in JS
                return taskDate < currentDate && task.isCompleted !== 1;
            } catch (e) {
                return false;
            }
        })
        .map(task => ({
            type: 'task' as const,
            data: task,
            // Use overwriteDate if available, otherwise use date
            days: differenceInDays(dateToXMLDate(currentDate), task.date)
        }))
        .sort((a, b) => a.days - b.days);
    
    // Filter items based on current view (Upcoming or All)
    const filteredGroups = showAllDates 
        ? groupedItems.filter(group => group.isPast) // Include Today in Previous Dates view
        : groupedItems.filter(group => !group.isPast || group.isToday); // Keep Today in Upcoming view too
    
    const pinClickHandler = () => {
        if (pinnedTab === "KeyDates") {
            dispatch(setPreference("PinRightSideTab", "toggle"));
        } else {
            dispatch(setPreference("PinRightSideTab", "KeyDates"));
        }
    };

    // Handle opening the edit modal
    const handleEditTask = (task) => {
        setTaskToEdit(task);
        
        // Use overwriteDate if available, otherwise use date
        setTaskDate(task.date);
        
        // Parse responsible users - check overwritePerson first, then userResponsible
        const responsibleUserString = task.userResponsible;
        
        const responsibleUsers = responsibleUserString ? 
            responsibleUserString.split(',').map(id => id.trim()).filter(id => id) : 
            [];
        
        setSelectedUsers(responsibleUsers);
        
        setShowEditModal(true);
    };

    // Handle saving the edited task
    const handleSaveTask = (updatedDescription?: string) => {
        if (!taskToEdit) return;
        
        // Validate: Check if at least one user is selected when not deactivating the task
        if (selectedUsers.length === 0) {
            setValidationError("Please assign at least one user to this task");
            return;
        }
        
        // Clear validation error
        setValidationError(null);

        // Check current assignments to see if anything changed
        const currentResponsibleUsers = taskToEdit.userResponsible;
        
        const currentDate = taskToEdit.date;
        // const currentOverwrite = taskToEdit.isOverwriteActivate !== undefined && taskToEdit.isOverwriteActivate !== null ? taskToEdit.isOverwriteActivate : 0;
        
        const usersChanged = selectedUsers.join(',') !== (currentResponsibleUsers || '');
        const dateChanged = taskDate !== currentDate;
        
        // Check if description changed (only for custom tasks with typeID > 999999)
        const descriptionChanged = updatedDescription && taskToEdit.typeID > 999999;
        
        // Create a new object with all properties from taskToEdit
        const updatedTask = { 
            ...taskToEdit,
            // Always update overwriteDate and overwritePerson if they are changed
            overwriteDate: dateChanged ? 1 : taskToEdit.overwriteDate,
            overwritePerson: usersChanged ? 1 : taskToEdit.overwritePerson,
            userResponsible: usersChanged ? selectedUsers.join(',') : taskToEdit.userResponsible,
            date: dateChanged ? taskDate : taskToEdit.date,
            // Update title if description changed (wrap in brackets like AddInfileTaskModal)
            title: descriptionChanged ? `(${updatedDescription})` : taskToEdit.title,
            // Ensure isActivated is preserved or set to 1 if not present
            isActivated: taskToEdit.isActivated || 1
        };

        // Create a new array with the updated task
        const updatedTasks = tasks.map(t => 
            t.typeID === taskToEdit.typeID ? updatedTask : { ...t }
        );

        // If task doesn't exist in current tasks, add it
        if (!updatedTasks.some(t => t.typeID === taskToEdit.typeID)) {
            updatedTasks.push(updatedTask);
        }

        // Update local state
        setTasks(updatedTasks);

        // Dispatch to Redux using the same pattern as handleCheckboxClick
        const tasksCopy = updatedTasks.map(tsk => ({ ...tsk }));
        dispatch(setInfileTasks(tasksCopy));

        runInnerFunction("SetPageDirty", null);
        
        // Close the modal
        setShowEditModal(false);
    };

    const handleRemoveTask = (task) => {
        
        // Create a copy of the current tasks
        let updatedTasks = [...tasks];
        
        // Find the task by ID
        const taskIndex = updatedTasks.findIndex(t => t.typeID=== task.typeID);
        
        if (taskIndex !== -1 ) {
            // Task exists in state - update it
            updatedTasks[taskIndex] = {
                ...updatedTasks[taskIndex],
                isCompleted: 1
            };
            setTasks(updatedTasks);

            updatedTasks[taskIndex] = {
                ...updatedTasks[taskIndex],
                isOverwriteActivate: 1,
                isActivated: 0
            };
            setTasks(updatedTasks);
        }
        
        // Update the tasks state
        setTasks(updatedTasks);
        
        // Also update Redux store with the newly updated tasks
        const tasksCopy = updatedTasks.map(tsk => ({ ...tsk }));
        dispatch(setInfileTasks(tasksCopy));
        runInnerFunction("SetPageDirty", null);
        setShowDeleteModal(false);
        setShowEditModal(false);
    };

    // Handle toggling a user selection
    const handleUserToggle = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    // Find user by ID
    const findUserById = (userId: string): any | undefined => {
        if (!userId) return undefined;
        
        const user = allLawyers.find(user => 
            user.SubUserId?.toString() === userId || 
            user.UserID?.toString() === userId ||
            user.LSUCID === userId
        );
        
        // Return null if user is not found (indicating deleted user)
        return user || null;
    };

    // Render a date item
    const renderDateItem = (dayMsg: any) => {
        return (
            <div className={styles.task + " " + styles.keyDate}>
                <Row className="gx-0 align-items-center">
                    <Col md="auto">
                        <Avatar icon="fa-solid fa-calendar-star" size="sm" color="#d68400" />
                    </Col>
                    <Col className="text-truncate">
                        <b>{dayMsg.title}</b>
                    </Col>
                </Row>
            </div>
        );
    };

    // Render a task item
    const renderTaskItem = (task, index, overdueInfo = null) => {
        // Parse responsible users if available
        // Check overwritePerson first, then userResponsible
        const responsibleUserString = task.userResponsible;
        
        const responsibleUsers = responsibleUserString ? 
            responsibleUserString.split(',').map(id => id.trim()).filter(id => id) : 
            [];
        
        // Generate tooltip content based on users
        let userTooltipContent = "";
        
        if (responsibleUsers.length === 0) {
            userTooltipContent = "Not assigned";
        } else {
            const userNames = responsibleUsers
                .map(id => {
                    const user = findUserById(id);
                    return user ? user.Name : null;
                })
                .filter(name => name !== null);
            
            const deletedUserCount = responsibleUsers.length - userNames.length;
            
            if (userNames.length === 0 && responsibleUsers.length > 0) {
                userTooltipContent = deletedUserCount === 1 ? "Assigned user deleted" : "All assigned users deleted";
            } else {
                userTooltipContent = userNames.join(", ");
                if (deletedUserCount > 0) {
                    userTooltipContent += `, (${deletedUserCount} deleted)`;
                }
            }
        }
        
        // Get the first responsible user for the avatar display
        const firstResponsibleUser = responsibleUsers.length > 0 ? 
            findUserById(responsibleUsers[0]) : 
            undefined;

        // Extract the second bracketed text from the title
        const fullTitle = task.title;
        const displayTitle = parseTaskTitle(fullTitle);

        // Handle checkbox click to toggle completion state
        const handleCheckboxClick = (e) => {
            e.stopPropagation(); // Prevent triggering the row's onClick handler
            
            // Create a copy of the current tasks
            let updatedTasks = [...tasks];
            
            // Find the task by ID
            const taskIndex = updatedTasks.findIndex(t => t.typeID=== task.typeID);
            
            if (taskIndex !== -1 ) {
                // Task exists in state - update it
                updatedTasks[taskIndex] = {
                    ...updatedTasks[taskIndex],
                    isCompleted: updatedTasks[taskIndex].isCompleted === 1 ? 0 : 1,
                    isActivated: updatedTasks[taskIndex].isActivated || 1 // Ensure isActivated is preserved
                };
            } else {
                // Task doesn't exist in state - add it
                updatedTasks = [
                    ...updatedTasks,
                    {
                        ...task,
                        isCompleted: task.isCompleted === 1 ? 0 : 1,
                        isActivated: task.isActivated || 1
                    }
                ];
            }
            
            // Update the tasks state
            setTasks(updatedTasks);

            // Also update Redux store with the newly updated tasks
            const tasksCopy = updatedTasks.map(tsk => ({ ...tsk }));
            dispatch(setInfileTasks(tasksCopy));
            runInnerFunction("SetPageDirty", null);
        };
        

        // Check if the task is completed (using only isCompleted)
        const isTaskCompleted = tasks.find(t => t.typeID === task.typeID)?.isCompleted === 1 || task.isCompleted === 1;

        // Check if the task is past due
        const isPastDue = () => {
            try {
                // Check for overwriteDate first, and use it if available
                const dateToUse = task.date;
                
                // Skip tasks with invalid dates
                if (dateToUse === null || dateToUse === undefined || dateToUse === "") {
                    return false;
                }
                
                // Parse the date parts explicitly to avoid timezone issues
                const [year, month, day] = dateToUse.split('-').map(Number);
                const taskDate = new Date(year, month - 1, day); // month is 0-indexed in JS
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                return taskDate < today && !isTaskCompleted;
            } catch (e) {
                return false;
            }
        };

        return (
            <div className={styles.task} onClick={(e) => {
                e.stopPropagation();
                handleEditTask(task);
            }}>
                {/* Add overdue label if present */}
                <Row className="gx-0">
                    <OverlayTrigger placement="bottom" delay={700} overlay={<Tooltip>{userTooltipContent}</Tooltip>}>
                        <Col md="auto">
                                {(() => {
                                    // Case 1: No users responsible
                                    if (responsibleUsers.length === 0) {
                                        return <Avatar icon="fa-light fa-user" size="sm" invert />;
                                    }
                                    
                                    // Case 2: Multiple users responsible
                                    if (responsibleUsers.length > 1) {
                                        return <Avatar icon="fa-solid fa-users" size="sm" />;
                                    }
                                    
                                    // Case 3: Single user with avatar
                                    if (firstResponsibleUser) {
                                        return (
                                            <Avatar initials={getInitials(firstResponsibleUser.Name || "")} size="sm" color={getUserColor(
                                                    firstResponsibleUser.SubUserId || firstResponsibleUser.UserID, 
                                                    SESSION_INFO.user?.id)} />
                                        );
                                    }
                                    
                                    // Fallback
                                    return <Avatar icon="fa-light fa-user" size="sm" invert />;
                                })()}
                        </Col>
                    </OverlayTrigger>

                    <Col className="text-truncate">
                        <OverlayTrigger placement="bottom" delay={700} overlay={<Tooltip>{displayTitle}</Tooltip>}>
                            <span>{displayTitle}</span> 
                        </OverlayTrigger>
                        {overdueInfo && (
                            <div className="text-danger small">
                                {overdueInfo.days} {overdueInfo.days === 1 ? 'day' : 'days'} overdue
                            </div>
                        )}
                    </Col>

                    <Col md="auto" className="align-right d-flex align-items-center" style={{ flexShrink: 0, width: isPastDue() ? '45px' : '25px' }}>
                        {/* Edit icon with tooltip */}
                        <OverlayTrigger placement="bottom" delay={700} overlay={<Tooltip>Edit task</Tooltip>}>
                            <i className="fa-regular fa-pencil hover-option"></i>
                        </OverlayTrigger>
                        
                        {/* Red flag for past due tasks */}
                        {isPastDue() && (
                            <i className="fa-solid fa-flag-swallowtail text-danger me-1"></i>
                        )}
                        
                        {/* Checkbox with tooltip */}
                        <OverlayTrigger placement="bottom" delay={700} overlay={<Tooltip>{isTaskCompleted ? 'Mark as incomplete' : 'Mark as complete'}</Tooltip>}>
                            <i onClick={handleCheckboxClick} className={isTaskCompleted ? "fa-solid fa-circle-check status-success" : "fa-regular fa-circle-check status-success"}></i>
                        </OverlayTrigger>
                    </Col>
                </Row>
            </div>
        );
    };

    // Render overdue task for Upcoming view
    const renderOverdueTask = (item) => {
        const task = item.data;
        const daysDiff = Math.abs(item.days);
        
        // Pass the overdue information to renderTaskItem
        return renderTaskItem(task, `overdue-${task.id}`, { days: daysDiff });
    };

    // Handle deleting a task in edit modal
    const handleDeleteTask = () => {
        if (!taskToEdit) return;
        setShowDeleteModal(true); // Show the confirmation modal
    };

    // Function to create and print a formatted table of tasks
    const handlePrint = () => {
        // Create a hidden div to hold our table for printing
        let printContainer = document.getElementById('keydates-print-container');
        if (!printContainer) {
            printContainer = document.createElement('div');
            printContainer.id = 'keydates-print-container';
            printContainer.style.display = 'none';
            document.body.appendChild(printContainer);
        }

        // Get the file number/record ID
        const fileNumber = DataReaderObj.getFileNumber() || 'Unknown File';
        
        // Start with the file number header
        let tableHTML = `
            <div style="margin-bottom: 10px; font-weight: bold; font-size: 16px;">
                File Number: ${fileNumber}
            </div>
            <table class="table table-bordered">
                <thead>
                    <tr>
                        <th>Task</th>
                        <th>Date</th>
                        <th>Users</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
        `;

        // Determine which data to print based on active tab
        if (activeTab === 'milestones') {
            // For milestones tab, include both date items and tasks
            const itemsToPrint = [];
            
            // Add overdue tasks first if in the Upcoming view
            if (!showAllDates && overdueIncompleteTasks.length > 0) {
                overdueIncompleteTasks.forEach(item => {
                    const task = item.data;
                    itemsToPrint.push({
                        type: 'task',
                        title: parseTaskTitle(task.title),
                        date: task.date,
                        users: getUsersString(task),
                        isCompleted: task.isCompleted === 1
                    });
                });
            }
            
            // Add grouped items
            filteredGroups.forEach(group => {
                group.items.forEach(item => {
                    if (item.type === 'date') {
                        // Add key date item
                        itemsToPrint.push({
                            type: 'date',
                            title: item.data.title,
                            date: item.data.day,
                            users: '',
                            isCompleted: false
                        });
                    } else {
                        // Add task item
                        const task = item.data;
                        itemsToPrint.push({
                            type: 'task',
                            title: parseTaskTitle(task.title),
                            date: task.date,
                            users: getUsersString(task),
                            isCompleted: task.isCompleted === 1
                        });
                    }
                });
            });
            
            // Sort items by date
            itemsToPrint.sort((a, b) => {
                // Parse the dates for comparison
                const dateA = new Date(a.date.split('-').map(Number)[0], a.date.split('-').map(Number)[1] - 1, a.date.split('-').map(Number)[2]);
                const dateB = new Date(b.date.split('-').map(Number)[0], b.date.split('-').map(Number)[1] - 1, b.date.split('-').map(Number)[2]);
                return dateA.getTime() - dateB.getTime();
            });
            
            // Add items to the table
            itemsToPrint.forEach(item => {
                tableHTML += `
                    <tr>
                        <td>${item.title}</td>
                        <td>${FormatDateDataType(item.date, "dfca")}</td>
                        <td>${item.users}</td>
                        <td>${item.type === 'date' ? 'Milestone' : (item.isCompleted ? 'Completed' : 'Pending')}</td>
                    </tr>
                `;
            });
        } else {
            // For tasklist tab, only include tasks
            // Filter tasks based on any applied user filter
            const filteredTasksForPrint = myTasks.filter(task => {
                // Skip invalid tasks
                if (!task) return false;
                
                // Display filter - only show tasks that are active and not overwritten
                if (task.isActivated !== 1) {
                    return false;
                }
                
                // If a user filter is applied
                if (filterByUser.length > 0) {
                    const responsibleUserString = task.userResponsible;
                    
                    if (!responsibleUserString) return false;
                    
                    const responsibleUsers = responsibleUserString.split(',').map(id => id.trim());
                    return responsibleUsers.includes(filterByUser[0]);
                }
                
                return true;
            });
            
            // Sort tasks by date
            const sortedTasks = [...filteredTasksForPrint].sort((a, b) => {
                const dateA = new Date(
                    (a.date).split('-').map(Number)[0], 
                    (a.date).split('-').map(Number)[1] - 1, 
                    (a.date).split('-').map(Number)[2]
                );
                const dateB = new Date(
                    (b.date).split('-').map(Number)[0], 
                    (b.date).split('-').map(Number)[1] - 1, 
                    (b.date).split('-').map(Number)[2]
                );
                return dateA.getTime() - dateB.getTime();
            });
            
            // Add all sorted tasks to the table
            sortedTasks.forEach(task => {
                tableHTML += `
                    <tr>
                        <td>${parseTaskTitle(task.title)}</td>
                        <td>${FormatDateDataType(task.date, "dfca")}</td>
                        <td>${getUsersString(task)}</td>
                        <td>${task.isCompleted === 1 ? 'Completed' : 'Pending'}</td>
                    </tr>
                `;
            });
        }

        tableHTML += `
                </tbody>
            </table>
        `;

        // Set the container's HTML and print
        printContainer.innerHTML = tableHTML;
        Dashboard.Print('keydates-print-container');
    };

    // Helper function to get a formatted string of users for a task
    const getUsersString = (task) => {
        const responsibleUserString = task.userResponsible;
        
        const responsibleUsers = responsibleUserString ? 
            responsibleUserString.split(',').map(id => id.trim()).filter(id => id) : 
            [];
        
        const userNames = responsibleUsers
            .map(id => {
                const user = findUserById(id);
                return user ? user.Name : null;
            })
            .filter(name => name !== null);
        
        const deletedUserCount = responsibleUsers.length - userNames.length;
        
        if (userNames.length === 0 && responsibleUsers.length > 0) {
            return deletedUserCount === 1 ? "Deleted User" : "All Users Deleted";
        }
        
        if (userNames.length === 0) {
            return "Not Assigned";
        }
        
        let result = userNames.join(", ");
        if (deletedUserCount > 0) {
            result += `, (${deletedUserCount} deleted)`;
        }
        
        return result;
    };

    // Add function to handle appointment editing
    const handleEditAppointment = (appointment) => {
        setAppointmentToEdit(appointment);
        setShowEditApptModal(true);
    };

    // Add function to handle saving appointment
    const handleSaveAppointment = async (updatedAppointment) => {
        try {
            const response = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/set/updateappointment?SessionID=${SESSION_INFO.sid}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedAppointment)
            });

            const data = await response.json();
            
            if (data.ok) {
                // Update local state
                setAppointments(prev => 
                    prev.map(apt => 
                        apt.calendarID === updatedAppointment.calendarID 
                            ? updatedAppointment 
                            : apt
                    )
                );
                setShowEditApptModal(false);
            } else {
                console.error('Error updating appointment:', data.error);
                throw new Error(data.error || 'Failed to update appointment');
            }
        } catch (error) {
            console.error('Error updating appointment:', error);
            throw error; // Re-throw so the modal can handle it
        }
    };
    const handleRemoveAppointment = async (appointmentToRemove) => {
        if (!appointmentToRemove) return;
        try {
            const response = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/set/deleteappointment?SessionID=${SESSION_INFO.sid}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(appointmentToRemove)
            });

            const data = await response.json();
            if (data.ok) {
                // Remove from local state
                console.log(data);
                setAppointments(prev => prev.filter(apt => apt.calendarID !== appointmentToRemove.calendarID));
                setShowEditApptModal(false);
                setShowDeleteAppModal(false);
                setAppointmentToEdit(null);
            } else {
                console.error('Error removing appointment:', data.error);
                throw new Error(data.error || 'Failed to remove appointment');
            }
        } catch (error) {
            console.error('Error removing appointment (network):', error);
            throw error;
        }
        
    };
    const handleDeleteAppointment = () => {
        if (!appointmentToEdit) return;
        setShowDeleteAppModal(true); // Show the confirmation modal
    };
    const renderAppointmentItem = (appointment, index) => {
        const isPrivate = appointment.privateItem;
        const maxLength = 30;
        const truncatedDescr = appointment.shortDescr && appointment.shortDescr.length > maxLength 
            ? appointment.shortDescr.substring(0, maxLength) + '...'
            : appointment.shortDescr;

        return (
            <div className={styles.task} onClick={(e) => {
                e.stopPropagation();
                handleEditAppointment(appointment);
            }}>
                <Row className="gx-0">
                    <Col md="auto">
                        <Avatar
                            invert
                            icon={"fa-solid fa-calendar-clock"} 
                            size="sm"
                        />
                    </Col>
                    <Col className="text-truncate">
                        <div className="d-flex align-items-center">
                            <span className="me-2">{truncatedDescr}</span>
                        </div>
                    </Col>
                    <Col md="auto" className="align-right d-flex align-items-center" style={{ flexShrink: 0, width: '25px' }}>
                        <OverlayTrigger placement="bottom" delay={700} overlay={<Tooltip>Edit appointment</Tooltip>}>
                            <i className="fa-regular fa-pencil hover-option"></i>
                        </OverlayTrigger>
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        // ****************** tabs ******************
        <div className={"toolbar-content " + drawerClasses} key="RightSideKeyDates">
            <Tab.Container id="key-dates-tabs" defaultActiveKey="milestones">
                <div className="toolbar-header" style={{padding: '0'}}>
                    <Nav variant="tabs">
                        <Nav.Item>
                            <Nav.Link eventKey="milestones" onClick={() => setActiveTab('milestones')}>
                                Milestones
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="tasklist" onClick={() => setActiveTab('tasklist')}>
                                Tasklist
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <div className="d-flex align-items-center">
                        <Button variant="link" className="p-0 me-2" onClick={handlePrint}>
                            <i className="fa-solid fa-action fa-print"></i>
                        </Button>
                        {!isPageInDR(props.page) &&
                            <OverlayTrigger placement="bottom" delay={700} overlay={
                                <Tooltip>{pinnedTab === "KeyDates" ? `Unset default` : `Set as default`}</Tooltip>
                                }>
                                <i style={{marginRight: '15px'}} className={`${pinnedTab === "KeyDates" ? "fa-solid" : "fa-regular"} fa-thumbtack`}
                                    onClick={pinClickHandler} />
                            </OverlayTrigger>
                        }
                    </div>
                </div>

                {/* ****************** Milestones ****************** */}
                <div className="toolbar-body">
                    <Tab.Content style={{height: '100%'}}>
                        <Tab.Pane eventKey="milestones">
                            <MilestonesTab
                                showAllDates={showAllDates}
                                setShowAllDates={setShowAllDates}
                                filteredGroups={filteredGroups}
                                overdueIncompleteTasks={overdueIncompleteTasks}
                                renderDateItem={renderDateItem}
                                renderTaskItem={renderTaskItem}
                                renderOverdueTask={renderOverdueTask}
                                renderAppointmentItem={renderAppointmentItem}
                            />
                        </Tab.Pane>
                        {/* ****************** Task List ****************** */}
                        <Tab.Pane eventKey="tasklist" style={{height: 'calc(100% - 22px)'}}>
                            <TasklistTab
                                SESSION_INFO={SESSION_INFO}
                                filterByUser={filterByUser}
                                setFilterByUser={setFilterByUser}
                                allLawyers={allLawyers}
                                myTasks={myTasks}
                                tasks={tasks}
                                setTasks={setTasks}
                                renderTaskItem={renderTaskItem}
                                isSameDay={isSameDay}
                                formatDateHeader={formatDateHeader}
                                getTaskStatus={getTaskStatus}
                            />
                        </Tab.Pane>
                    </Tab.Content>
                </div>
            </Tab.Container>

            {/* Edit Task Modal Component */}
            <EditTaskModal
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                taskToEdit={taskToEdit}
                taskDate={taskDate}
                setTaskDate={setTaskDate}
                selectedUsers={selectedUsers}
                validationError={validationError}
                setValidationError={setValidationError}
                handleUserToggle={handleUserToggle}
                handleSaveTask={handleSaveTask}
                handleDeleteTask={handleDeleteTask}
                handleRemoveTask={handleRemoveTask}
                showDeleteModal={showDeleteModal}
                setShowDeleteModal={setShowDeleteModal}
                allLawyers={allLawyers}
                myTasks={myTasks}
            />

            {/* Add EditApptModal */}
            <EditApptModal
                showEditModal={showEditApptModal}
                setShowEditModal={setShowEditApptModal}
                appointmentToEdit={appointmentToEdit}
                handleSaveAppointment={handleSaveAppointment}
                handleRemoveAppointment={handleRemoveAppointment}
                handleDeleteAppointment={handleDeleteAppointment}
                setShowDeleteAppModal={setShowDeleteAppModal}  
                showDeleteAppModal={showDeleteAppModal}
            />
        </div>
    );
};